'use strict'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  makeStyles,
  Paper,
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Switch,
  Chip,
  CircularProgress,
  FormControlLabel,
  Collapse
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import RefreshIcon from '@material-ui/icons/Refresh'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import PropertyRowDetail from './PropertyRowDetail'
import { getCrewColor, getRouteColor } from './crewColors'

const DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

const sortDays = (days = []) => {
  return [...new Set(days.filter(Boolean))]
    .sort((a, b) => DAY_OPTIONS.indexOf(a) - DAY_OPTIONS.indexOf(b))
}

const useStyles = makeStyles(theme => ({
  metricsCard: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2)
  },
  metricsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center'
  },
  metricBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 80
  },
  metricValue: {
    fontSize: '1.25rem',
    fontWeight: 700
  },
  metricLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1)
  },
  search: {
    minWidth: 260
  },
  tableWrapper: {
    overflowX: 'auto'
  },
  table: {
    minWidth: 760
  },
  cell: {
    padding: '6px 10px',
    verticalAlign: 'top'
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    whiteSpace: 'nowrap',
    padding: '8px 10px'
  },
  nameCell: {
    maxWidth: 260
  },
  addressText: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.3
  },
  freqCell: {
    maxWidth: 180
  },
  crewDot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 6,
    verticalAlign: 'middle'
  },
  expandBtn: {
    padding: 4
  },
  detailRow: {
    backgroundColor: theme.palette.grey[50]
  },
  crewMetricsTable: {
    marginTop: theme.spacing(1.5),
    minWidth: 700
  },
  crewMetricsCell: {
    padding: '4px 10px'
  },
  crewMetricsHeader: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    padding: '6px 10px'
  }
}))

export default function PropertiesTab ({
  url,
  selectedScenarioId,
  isScenarioEditable,
  crews,
  crewIds,
  routes,
  routeIds,
  onSnackbar,
  onRefreshMetrics,
  onRefreshDiff,
  onCountsChange
}) {
  const classes = useStyles()

  const [rows, setRows] = useState([])
  const [originalRowsById, setOriginalRowsById] = useState({})
  const [loading, setLoading] = useState(true)
  const [expandedRowId, setExpandedRowId] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [onlyVip, setOnlyVip] = useState(false)
  const [onlyLocked, setOnlyLocked] = useState(false)
  const [onlyNeedsReview, setOnlyNeedsReview] = useState(false)
  const [onlyUnassigned, setOnlyUnassigned] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [savingBySiteId, setSavingBySiteId] = useState({})
  const [movingBySiteId, setMovingBySiteId] = useState({})
  const [moveTargetBySiteId, setMoveTargetBySiteId] = useState({})
  const [suggestingBySiteId, setSuggestingBySiteId] = useState({})
  const [scenarioMetrics, setScenarioMetrics] = useState(null)
  const [loadingMetrics, setLoadingMetrics] = useState(false)
  const [bulkSuggesting, setBulkSuggesting] = useState(false)
  const [resyncingBySiteId, setResyncingBySiteId] = useState({})

  const crewNameById = useMemo(() => {
    const map = {}
    crews.forEach(crew => { map[crew._id] = crew.name })
    return map
  }, [crews])

  const routeNameById = useMemo(() => {
    const map = {}
    ;(routes || []).forEach(r => { map[r._id] = r.name })
    return map
  }, [routes])

  const fetchData = useCallback(async (scenarioId) => {
    setLoading(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/properties`, {
        params: scenarioId ? { scenarioId } : undefined
      })
      const payload = response.data || {}
      const nextRows = payload.properties || []
      const nextOriginalRowsById = {}
      nextRows.forEach(row => {
        nextOriginalRowsById[row.aspirePropertyId] = {
          lockedCrewId: row.lockedCrewId || '',
          isVip: !!row.isVip,
          frequency: row.frequency || '',
          preferredDay: row.preferredDay || '',
          preferredDays: sortDays(row.preferredDays || []),
          weekType: row.weekType || ''
        }
      })
      setRows(nextRows)
      setOriginalRowsById(nextOriginalRowsById)
      setMoveTargetBySiteId(nextRows.reduce((acc, row) => {
        acc[row.aspirePropertyId] = row.assignedRouteId || row.assignedCrewId || ''
        return acc
      }, {}))
    } catch (err) {
      console.error('Error loading territory properties:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to load properties', 'error')
    } finally {
      setLoading(false)
    }
  }, [url, onSnackbar])

  const fetchMetrics = useCallback(async (scenarioId) => {
    setLoadingMetrics(true)
    try {
      const metricsUrl = scenarioId
        ? `${url}/api/hmi/territory/scenarios/${scenarioId}/metrics`
        : `${url}/api/hmi/territory/metrics`
      const response = await axios.get(metricsUrl)
      setScenarioMetrics({
        crews: response.data?.crews || [],
        routes: response.data?.routes || [],
        fleet: response.data?.fleet || null
      })
    } catch (err) {
      console.error('Error loading metrics:', err)
      setScenarioMetrics(null)
    } finally {
      setLoadingMetrics(false)
    }
  }, [url])

  useEffect(() => {
    fetchData(selectedScenarioId || null)
    fetchMetrics(selectedScenarioId || null)
  }, [selectedScenarioId, fetchData, fetchMetrics])

  useEffect(() => {
    if (onCountsChange) {
      onCountsChange({
        total: rows.length,
        vip: rows.filter(r => r.isVip).length,
        locked: rows.filter(r => !!r.lockedCrewId).length,
        unassigned: rows.filter(r => !r.assignedRouteId && !r.assignedCrewId).length,
        anchors: rows.filter(r => r.anchor && r.anchor.isAnchor).length
      })
    }
  }, [rows, onCountsChange])

  const isRowDirty = useCallback((row) => {
    const original = originalRowsById[row.aspirePropertyId]
    if (!original) return false
    const rowDays = sortDays(row.preferredDays || [])
    const origDays = sortDays(original.preferredDays || [])
    return (row.lockedCrewId || '') !== (original.lockedCrewId || '') ||
      !!row.isVip !== !!original.isVip ||
      (row.frequency || '') !== (original.frequency || '') ||
      (row.preferredDay || '') !== (original.preferredDay || '') ||
      (row.weekType || '') !== (original.weekType || '') ||
      rowDays.join('|') !== origDays.join('|')
  }, [originalRowsById])

  const updateRowField = (aspirePropertyId, field, value) => {
    setRows(prev => prev.map(row => {
      if (row.aspirePropertyId !== aspirePropertyId) return row
      return { ...row, [field]: value }
    }))
  }

  const updateLegacyFrequency = (aspirePropertyId, nextFrequency) => {
    setRows(prev => prev.map(row => {
      if (row.aspirePropertyId !== aspirePropertyId) return row
      const updated = { ...row, frequency: nextFrequency || '' }
      if (nextFrequency === 'Every Other Day (M-W-F)') {
        updated.preferredDays = ['Monday', 'Wednesday', 'Friday']
        updated.preferredDay = 'Monday'
        updated.weekType = ''
      } else if (nextFrequency === 'Twice a Week') {
        if (!Array.isArray(updated.preferredDays) || !updated.preferredDays.length) {
          updated.preferredDays = updated.preferredDay ? [updated.preferredDay] : ['Monday', 'Thursday']
        }
        updated.weekType = ''
      } else if (nextFrequency === 'Weekly') {
        updated.preferredDay = updated.preferredDay || 'Monday'
        updated.preferredDays = []
        updated.weekType = ''
      } else if (nextFrequency === 'Bi-Weekly') {
        updated.preferredDay = updated.preferredDay || 'Monday'
        updated.preferredDays = []
      } else {
        updated.weekType = ''
      }
      return updated
    }))
  }

  const isLiveMode = !selectedScenarioId

  const saveRow = async (row) => {
    if (!isRowDirty(row)) return
    setSavingBySiteId(prev => ({ ...prev, [row.aspirePropertyId]: true }))
    try {
      const params = selectedScenarioId ? { scenarioId: selectedScenarioId } : {}
      const response = await axios.patch(
        `${url}/api/hmi/territory/properties/${row.aspirePropertyId}`,
        {
          contractId: row.contractId,
          lockedCrewId: row.lockedCrewId || null,
          isVip: !!row.isVip,
          frequency: row.frequency || null,
          preferredDay: row.preferredDay || null,
          preferredDays: sortDays(row.preferredDays || []),
          weekType: row.weekType || null
        },
        { params }
      )
      const updated = response.data?.property
      if (updated) {
        setRows(prev => prev.map(item => item.aspirePropertyId === row.aspirePropertyId ? { ...item, ...updated } : item))
        setOriginalRowsById(prev => ({
          ...prev,
          [row.aspirePropertyId]: {
            lockedCrewId: updated.lockedCrewId || '',
            isVip: !!updated.isVip,
            frequency: updated.frequency || '',
            preferredDay: updated.preferredDay || '',
            preferredDays: sortDays(updated.preferredDays || []),
            weekType: updated.weekType || ''
          }
        }))
      } else {
        setOriginalRowsById(prev => ({
          ...prev,
          [row.aspirePropertyId]: {
            lockedCrewId: row.lockedCrewId || '',
            isVip: !!row.isVip,
            frequency: row.frequency || '',
            preferredDay: row.preferredDay || '',
            preferredDays: sortDays(row.preferredDays || []),
            weekType: row.weekType || ''
          }
        }))
      }
      onSnackbar(isLiveMode ? 'Live override saved' : 'Draft updated', 'success')
      fetchMetrics(selectedScenarioId || null)
      if (onRefreshMetrics) onRefreshMetrics()
      if (onRefreshDiff) onRefreshDiff()
    } catch (err) {
      console.error('Error updating territory property:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to update property', 'error')
    } finally {
      setSavingBySiteId(prev => ({ ...prev, [row.aspirePropertyId]: false }))
    }
  }

  const moveRow = async (row) => {
    if (!selectedScenarioId || !isScenarioEditable) return
    const moveTarget = moveTargetBySiteId[row.aspirePropertyId] || ''
    if (!moveTarget) return

    // Determine if moveTarget is a routeId or crewId
    const isRouteTarget = (routes || []).some(r => r._id === moveTarget)
    const currentId = row.assignedRouteId || row.assignedCrewId || ''
    if (moveTarget === currentId) return

    setMovingBySiteId(prev => ({ ...prev, [row.aspirePropertyId]: true }))
    try {
      const body = { aspirePropertyId: row.aspirePropertyId }
      if (isRouteTarget) {
        body.toRouteId = moveTarget
      } else {
        body.toCrewId = moveTarget
      }
      const response = await axios.post(
        `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`,
        body
      )
      const updatedAssignment = response.data?.assignment || null
      setRows(prev => prev.map(item => {
        if (item.aspirePropertyId !== row.aspirePropertyId) return item
        const assignedCrewId = updatedAssignment?.assignedCrewId || null
        const assignedRouteId = updatedAssignment?.assignedRouteId || null
        const routeName = assignedRouteId ? (routeNameById[assignedRouteId] || null) : null
        return {
          ...item,
          assignedCrewId,
          assignedRouteId,
          assignedCrewName: crewNameById[assignedCrewId] || 'Unassigned',
          assignedRouteName: routeName
        }
      }))
      setMoveTargetBySiteId(prev => ({ ...prev, [row.aspirePropertyId]: moveTarget }))
      const targetName = isRouteTarget
        ? (routeNameById[moveTarget] || 'selected route')
        : (crewNameById[moveTarget] || 'selected crew')
      onSnackbar(`Moved ${row.propertyName || `Aspire #${row.aspirePropertyId}`} to ${targetName}`, 'success')
      fetchMetrics(selectedScenarioId)
      if (onRefreshMetrics) onRefreshMetrics()
      if (onRefreshDiff) onRefreshDiff()
    } catch (err) {
      console.error('Error moving scenario property:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to move property', 'error')
    } finally {
      setMovingBySiteId(prev => ({ ...prev, [row.aspirePropertyId]: false }))
    }
  }

  const suggestNearestForRow = async (row) => {
    if (!selectedScenarioId || !row?.aspirePropertyId) return
    const propId = row.aspirePropertyId
    setSuggestingBySiteId(prev => ({ ...prev, [propId]: true }))
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/suggest-nearest`, {
        aspirePropertyIds: [propId],
        includeAssigned: true,
        limit: 1
      })
      const suggestion = response.data?.suggestions?.[0]
      if (!suggestion?.suggestedCrewId && !suggestion?.suggestedRouteId) {
        onSnackbar('No suggestion available for this property', 'info')
        return
      }
      // Prefer route-based suggestion
      const suggestedTarget = suggestion.suggestedRouteId || suggestion.suggestedCrewId
      setMoveTargetBySiteId(prev => ({ ...prev, [propId]: suggestedTarget }))
      const hasDriveMin = Number.isFinite(Number(suggestion.driveMinutes))
      const metricLabel = hasDriveMin ? `${Math.round(Number(suggestion.driveMinutes))} min` : `${suggestion.distanceMiles || 0} mi`
      const methodLabel = suggestion.selectionMethod === 'ors_matrix' ? 'ORS' : 'Haversine'
      onSnackbar(
        `Suggested ${suggestion.suggestedCrewName || 'crew'} (${metricLabel}, ${methodLabel})`,
        suggestion.isAmbiguous ? 'warning' : 'success'
      )
    } catch (err) {
      console.error('Error getting nearest-crew suggestion:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to suggest nearest crew', 'error')
    } finally {
      setSuggestingBySiteId(prev => ({ ...prev, [propId]: false }))
    }
  }

  const bulkSuggestUnassigned = async () => {
    if (!selectedScenarioId || !isScenarioEditable) return
    const unassigned = rows.filter(r => !r.assignedRouteId && !r.assignedCrewId)
    if (!unassigned.length) {
      onSnackbar('All properties are already assigned', 'info')
      return
    }
    setBulkSuggesting(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/suggest-nearest`, {
        aspirePropertyIds: unassigned.map(r => r.aspirePropertyId),
        includeAssigned: false,
        limit: 200
      })
      const suggestions = response.data?.suggestions || []
      if (!suggestions.length) {
        onSnackbar('No suggestions available for unassigned properties', 'info')
        return
      }
      const nextTargets = {}
      suggestions.forEach(s => {
        const target = s.suggestedRouteId || s.suggestedCrewId
        if (target) {
          nextTargets[s.aspirePropertyId] = target
        }
      })
      setMoveTargetBySiteId(prev => ({ ...prev, ...nextTargets }))
      onSnackbar(`Suggested crews for ${Object.keys(nextTargets).length} of ${unassigned.length} unassigned properties`, 'success')
    } catch (err) {
      console.error('Error bulk suggesting:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to bulk suggest', 'error')
    } finally {
      setBulkSuggesting(false)
    }
  }

  const resyncProperty = async (row) => {
    const propId = row.aspirePropertyId
    if (!propId) return
    setResyncingBySiteId(prev => ({ ...prev, [propId]: true }))
    try {
      const response = await axios.post(`${url}/api/aspire/routing/resync-property`, {
        propertyId: propId
      })
      if (response.data?.success) {
        onSnackbar(`Re-synced ${row.propertyName || `#${propId}`} from Aspire`, 'success')
        // Refresh the property list to pick up updated data
        fetchData(selectedScenarioId || null)
        fetchMetrics(selectedScenarioId || null)
      } else {
        onSnackbar(response.data?.error || 'Resync failed', 'error')
      }
    } catch (err) {
      console.error('Error resyncing property:', err)
      onSnackbar(err.response?.data?.error || err.message || 'Failed to resync property', 'error')
    } finally {
      setResyncingBySiteId(prev => ({ ...prev, [propId]: false }))
    }
  }

  const filteredRows = useMemo(() => {
    let next = [...rows]
    if (searchText.trim()) {
      const search = searchText.trim().toLowerCase()
      next = next.filter(row => {
        const haystack = [row.propertyName, row.clientName, row.address, row.city, row.customerType, row.frequency, row.assignedRouteName, row.assignedCrewName, row.lockedCrewName]
          .filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(search)
      })
    }
    if (onlyVip) next = next.filter(row => row.isVip)
    if (onlyLocked) next = next.filter(row => !!row.lockedCrewId)
    if (onlyNeedsReview) next = next.filter(row => row.frequencyConfidence === 'needs_review' || row.frequencyCode === 'UNKNOWN')
    if (onlyUnassigned) next = next.filter(row => !row.assignedRouteId && !row.assignedCrewId)
    return next
  }, [rows, searchText, onlyVip, onlyLocked, onlyNeedsReview, onlyUnassigned])

  const paginatedRows = useMemo(() => {
    return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])

  const fleet = scenarioMetrics?.fleet

  const buildFrequencyLabel = (row) => {
    const parts = [row.frequency || 'Unspecified']
    if (row.preferredDay && (row.frequency === 'Weekly' || row.frequency === 'Bi-Weekly')) {
      parts.push(row.preferredDay.slice(0, 3))
    }
    if (Array.isArray(row.preferredDays) && row.preferredDays.length > 0 && row.frequency === 'Twice a Week') {
      parts.push(sortDays(row.preferredDays).map(d => d.slice(0, 3)).join(', '))
    }
    if (row.weekType) {
      parts.push(`(Wk ${row.weekType})`)
    }
    return parts.join(' ')
  }

  const getLinkLabel = (row) => {
    if (row.linkStatus === 'agreement_linked') return 'Linked'
    if (row.linkStatus === 'jobsite_only') return 'Jobsite'
    return 'Unmapped'
  }

  const getLinkColor = (row) => {
    if (row.linkStatus === 'agreement_linked') return 'primary'
    return 'default'
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <div>
      {/* Metrics Summary */}
      {fleet && (
        <Paper variant="outlined" className={classes.metricsCard}>
          <div className={classes.metricsRow}>
            {loadingMetrics && <CircularProgress size={16} />}
            {isLiveMode && (
              <Chip size="small" label="LIVE" style={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 700 }} />
            )}
            <div className={classes.metricBox}>
              <Typography className={classes.metricValue}>{(scenarioMetrics?.routes || []).filter(r => r.routeId !== 'unassigned').length || fleet.crewCount || 0}</Typography>
              <Typography className={classes.metricLabel}>Routes</Typography>
            </div>
            <div className={classes.metricBox}>
              <Typography className={classes.metricValue}>{fleet.totalProperties || 0}</Typography>
              <Typography className={classes.metricLabel}>Properties</Typography>
            </div>
            <div className={classes.metricBox}>
              <Typography className={classes.metricValue}>{fleet.totalVip || 0}</Typography>
              <Typography className={classes.metricLabel}>VIP</Typography>
            </div>
            <div className={classes.metricBox}>
              <Typography className={classes.metricValue} style={{ color: rows.filter(r => !r.assignedRouteId && !r.assignedCrewId).length > 0 ? '#f44336' : undefined }}>
                {rows.filter(r => !r.assignedRouteId && !r.assignedCrewId).length}
              </Typography>
              <Typography className={classes.metricLabel}>Unassigned</Typography>
            </div>
            <div className={classes.metricBox}>
              <Typography className={classes.metricValue}>{fleet.totalLockedCount || 0}</Typography>
              <Typography className={classes.metricLabel}>Locked</Typography>
            </div>
            <div className={classes.metricBox}>
              <Typography className={classes.metricValue}>{fleet.totalAnchorCount || 0}</Typography>
              <Typography className={classes.metricLabel}>Anchors</Typography>
            </div>
            <div className={classes.metricBox}>
              <Typography className={classes.metricValue}>{fleet.totalServiceMinutes || 0}</Typography>
              <Typography className={classes.metricLabel}>Svc min</Typography>
            </div>
            <div className={classes.metricBox}>
              <Typography className={classes.metricValue} style={{ color: (fleet.totalActualServiceMinutes || 0) !== (fleet.totalServiceMinutes || 0) ? '#e65100' : undefined }}>
                {fleet.totalActualServiceMinutes || 0}
              </Typography>
              <Typography className={classes.metricLabel}>Actual Svc</Typography>
            </div>
            <div className={classes.metricBox}>
              <Typography className={classes.metricValue}>{fleet.totalDriveMinutes || 0}</Typography>
              <Typography className={classes.metricLabel}>Drive min</Typography>
            </div>
            <div className={classes.metricBox}>
              <Typography className={classes.metricValue}>{((fleet.totalOTMinutes || 0) / 60).toFixed(1)}</Typography>
              <Typography className={classes.metricLabel}>OT hrs</Typography>
            </div>
          </div>

          {/* Route metrics mini-table */}
          {Array.isArray(scenarioMetrics?.routes) && scenarioMetrics.routes.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <Table className={classes.crewMetricsTable} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.crewMetricsHeader}>Route</TableCell>
                    <TableCell className={classes.crewMetricsHeader}>Props</TableCell>
                    <TableCell className={classes.crewMetricsHeader}>VIP</TableCell>
                    <TableCell className={classes.crewMetricsHeader}>Service</TableCell>
                    <TableCell className={classes.crewMetricsHeader}>Actual</TableCell>
                    <TableCell className={classes.crewMetricsHeader}>Drive</TableCell>
                    <TableCell className={classes.crewMetricsHeader}>Miles</TableCell>
                    <TableCell className={classes.crewMetricsHeader}>Total</TableCell>
                    <TableCell className={classes.crewMetricsHeader}>OT</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scenarioMetrics.routes
                    .filter(item => item.routeId !== 'unassigned')
                    .sort((a, b) => Number(b.weeklyTotalMinutes || 0) - Number(a.weeklyTotalMinutes || 0))
                    .map(item => (
                      <TableRow key={item.routeId}>
                        <TableCell className={classes.crewMetricsCell}>
                          <span className={classes.crewDot} style={{ backgroundColor: getRouteColor(item.routeId, routeIds || []) }} />
                          {item.routeName}
                        </TableCell>
                        <TableCell className={classes.crewMetricsCell}>{item.propertyCount || 0}</TableCell>
                        <TableCell className={classes.crewMetricsCell}>{item.vipCount || 0}</TableCell>
                        <TableCell className={classes.crewMetricsCell}>{item.weeklyServiceMinutes || 0}</TableCell>
                        <TableCell className={classes.crewMetricsCell} style={{ color: (item.actualWeeklyServiceMinutes || 0) !== (item.weeklyServiceMinutes || 0) ? '#e65100' : undefined }}>{item.actualWeeklyServiceMinutes || 0}</TableCell>
                        <TableCell className={classes.crewMetricsCell}>{item.estimatedWeeklyDriveMinutes || 0}</TableCell>
                        <TableCell className={classes.crewMetricsCell}>{item.estimatedWeeklyDriveMiles || 0}</TableCell>
                        <TableCell className={classes.crewMetricsCell}>{item.weeklyTotalMinutes || 0}</TableCell>
                        <TableCell className={classes.crewMetricsCell}>{item.estimatedWeeklyOTMinutes || 0}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Paper>
      )}

      {/* Filter Bar */}
      <Paper variant="outlined" className={classes.filterBar}>
        <TextField
          className={classes.search}
          variant="outlined"
          size="small"
          placeholder="Search properties..."
          value={searchText}
          onChange={(e) => { setSearchText(e.target.value); setPage(0) }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon /></InputAdornment>
            )
          }}
        />
        <FormControlLabel
          control={<Switch checked={onlyVip} onChange={(e) => { setOnlyVip(e.target.checked); setPage(0) }} size="small" />}
          label="VIP"
        />
        <FormControlLabel
          control={<Switch checked={onlyLocked} onChange={(e) => { setOnlyLocked(e.target.checked); setPage(0) }} size="small" />}
          label="Locked"
        />
        <FormControlLabel
          control={<Switch checked={onlyNeedsReview} onChange={(e) => { setOnlyNeedsReview(e.target.checked); setPage(0) }} size="small" />}
          label="Needs review"
        />
        <FormControlLabel
          control={<Switch checked={onlyUnassigned} onChange={(e) => { setOnlyUnassigned(e.target.checked); setPage(0) }} size="small" />}
          label="Unassigned"
        />
        {onlyUnassigned && (
          <Button
            variant="outlined"
            size="small"
            disabled={!isScenarioEditable || bulkSuggesting}
            onClick={bulkSuggestUnassigned}
          >
            {bulkSuggesting ? 'Suggesting...' : 'Suggest Routes'}
          </Button>
        )}
        <IconButton size="small" onClick={() => { fetchData(selectedScenarioId || null); fetchMetrics(selectedScenarioId || null) }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="textSecondary" style={{ marginLeft: 'auto' }}>
          Showing {filteredRows.length} of {rows.length}
        </Typography>
      </Paper>

      {/* Property Table */}
      <Paper variant="outlined">
        <div className={classes.tableWrapper}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.cell} ${classes.headerCell}`} style={{ width: 260 }}>Property</TableCell>
                <TableCell className={`${classes.cell} ${classes.headerCell}`} style={{ width: 90 }}>Status</TableCell>
                <TableCell className={`${classes.cell} ${classes.headerCell}`} style={{ width: 160 }}>Frequency</TableCell>
                <TableCell className={`${classes.cell} ${classes.headerCell}`} style={{ width: 130 }}>Route</TableCell>
                <TableCell className={`${classes.cell} ${classes.headerCell}`} style={{ width: 50 }}>VIP</TableCell>
                <TableCell className={`${classes.cell} ${classes.headerCell}`} style={{ width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map(row => {
                const isExpanded = expandedRowId === row.aspirePropertyId
                const dirty = isRowDirty(row)
                const crewColor = row.assignedRouteId
                  ? getRouteColor(row.assignedRouteId, routeIds || [])
                  : getCrewColor(row.assignedCrewId, crewIds)

                return (
                  <React.Fragment key={row.aspirePropertyId}>
                    <TableRow
                      hover
                      onClick={() => setExpandedRowId(isExpanded ? null : row.aspirePropertyId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell className={`${classes.cell} ${classes.nameCell}`}>
                        <Typography variant="body2">{row.propertyName || '-'}</Typography>
                        <Typography className={classes.addressText}>
                          {[row.address, row.city, `#${row.aspirePropertyId}`].filter(Boolean).join(' \u00B7 ')}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Chip size="small" label={getLinkLabel(row)} color={getLinkColor(row)} />
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.freqCell}`}>
                        <Typography variant="body2" style={{ fontSize: '0.82rem' }}>{buildFrequencyLabel(row)}</Typography>
                        {(row.frequencyConfidence === 'needs_review' || row.frequencyCode === 'UNKNOWN') && (
                          <Chip size="small" color="secondary" label="Needs review" style={{ marginTop: 2 }} />
                        )}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Box display="flex" alignItems="center">
                          <span className={classes.crewDot} style={{ backgroundColor: crewColor }} />
                          <Typography variant="body2" style={{ fontSize: '0.82rem' }}>
                            {row.assignedRouteName || row.assignedCrewName || 'Unassigned'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <Switch
                          checked={!!row.isVip}
                          onChange={(e) => {
                            e.stopPropagation()
                            updateRowField(row.aspirePropertyId, 'isVip', e.target.checked)
                          }}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          color="secondary"
                          disabled={!isScenarioEditable && !isLiveMode}
                        />
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <IconButton className={classes.expandBtn} size="small">
                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow className={classes.detailRow}>
                        <TableCell colSpan={6} style={{ padding: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <PropertyRowDetail
                              row={row}
                              crews={crews}
                              routes={routes}
                              isEditable={isScenarioEditable || isLiveMode}
                              isLiveMode={isLiveMode}
                              isDirty={dirty}
                              isSaving={!!savingBySiteId[row.aspirePropertyId]}
                              isMoving={!!movingBySiteId[row.aspirePropertyId]}
                              isSuggesting={!!suggestingBySiteId[row.aspirePropertyId]}
                              isResyncing={!!resyncingBySiteId[row.aspirePropertyId]}
                              moveTarget={moveTargetBySiteId[row.aspirePropertyId] || row.assignedCrewId || ''}
                              onUpdateField={updateRowField}
                              onUpdateFrequency={updateLegacyFrequency}
                              onMoveTargetChange={(id, val) => setMoveTargetBySiteId(prev => ({ ...prev, [id]: val }))}
                              onMove={moveRow}
                              onSuggest={suggestNearestForRow}
                              onSave={saveRow}
                              onResync={resyncProperty}
                              onCollapse={() => setExpandedRowId(null)}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
              {paginatedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary" style={{ padding: 16 }}>No properties match your filters</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
        />
      </Paper>
    </div>
  )
}
